/**
 * Мелочи, из которых собраны все экраны (тикет T18b).
 *
 * Они здесь ради одного: чтобы доступность нельзя было забыть. Подпись поля связана с
 * полем не «визуально рядом», а по-настоящему; беда объявляется голосом сама; у кнопки
 * есть наименьший размер из Настроек. Собери кто-нибудь такое же поле руками — он
 * непременно про что-нибудь из этого забудет, и заметит это только тот, кто пользуется
 * экранным диктором, то есть уже пострадавший.
 */
import { useId, useState } from 'react';
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';
import { useT } from '../state/settings.js';

type ButtonLook = 'primary' | 'plain' | 'danger' | 'quiet';

const BUTTON_LOOK: Readonly<Record<ButtonLook, string>> = {
  primary: 'bg-accent text-accent-ink border-transparent hover:opacity-90',
  plain: 'bg-surface text-ink border-line hover:bg-surface-2',
  danger: 'bg-danger-soft text-danger border-danger hover:opacity-90',
  quiet: 'bg-transparent text-ink-soft border-transparent hover:bg-surface-2 hover:text-ink',
};

export function Button({
  look = 'plain',
  className = '',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { readonly look?: ButtonLook }): ReactNode {
  return (
    <button
      // Тип указан всегда: без него кнопка внутри формы молча отправляет форму, и «убрать
      // Блок» однажды сработает как «войти».
      type="button"
      {...rest}
      className={`inline-flex items-center justify-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${BUTTON_LOOK[look]} ${className}`}
    />
  );
}

/**
 * Значок глаза. Рисуем сами, а не берём готовый набор: значок обязан тянуться вместе с
 * размером текста из Настроек и краситься цветом кнопки, а картинка-эмодзи у каждой
 * системы своя и в высоком контрасте теряется.
 */
function EyeIcon({ crossed }: { readonly crossed: boolean }): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="size-[1.15em]"
    >
      <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12Z" />
      <circle cx="12" cy="12" r="2.8" />
      {/* Черта идёт из угла в угол через зрачок: пройди она мимо середины, при мелком
          размере читалась бы как случайный штрих, а не как «пароль сейчас открыт». */}
      {crossed ? <path d="M3.5 20.5 20.5 3.5" /> : null}
    </svg>
  );
}

/**
 * Поле с подписью. Подпись связана с полем через общий опознаватель — тогда её читают
 * вслух вместе с полем, и нажатие по подписи ставит курсор в поле.
 *
 * Пароль умеет показываться: набирать вслепую длинный пароль — значит ошибаться, а тому,
 * кто плохо видит, звёздочки не оставляют вовсе никакой возможности себя проверить.
 * Кнопка стоит внутри самой строки ввода, у её дальнего края (правка 2026-08-27): кнопка
 * во всю ширину под полем занимала пол-экрана и читалась как отдельное действие формы, а
 * не как ручка этого поля. Слов на кнопке нет, но немой она не стала — подпись целиком
 * висит на ней для диктора и всплывает подсказкой, а сторона у неё логическая, поэтому на
 * иврите кнопка сама уезжает к другому краю. Меньше наименьшего размера из Настроек она
 * не делается, и ровно на её ширину поле отодвигает свой текст — набранное под неё не
 * уедет. Показ живёт только пока открыт экран — вернувшись, человек снова видит звёздочки,
 * а не свой пароль, забытый на виду.
 */
export function Field({
  label,
  hint,
  error,
  type,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & {
  readonly label: string;
  readonly hint?: string;
  readonly error?: string | null;
}): ReactNode {
  const t = useT();
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const described = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');
  const secret = type === 'password';
  const [shown, setShown] = useState(false);
  const visible = secret && shown;

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm text-ink-soft">
        {label}
      </label>
      <div className="relative flex">
        <input
          id={id}
          {...rest}
          // Свой род поля — после чужих свойств: попади однажды `type` в пробрасываемые
          // свойства, он молча отменил бы показ пароля, и заметили бы это не сразу.
          type={visible ? 'text' : type}
          aria-invalid={error ? true : undefined}
          aria-describedby={described || undefined}
          className={`w-full rounded-md border border-line bg-surface px-3 py-2 text-ink ${
            secret ? 'ktm-secret-input' : ''
          }`}
        />
        {secret ? (
          // Подпись меняется вместе с состоянием — это не «кнопка на прежнем месте стала
          // другой», а единственный способ сказать вслух, что произойдёт по нажатию.
          // Значок один и тот же, перечёркнутым он становится, когда пароль открыт: так
          // видно текущее положение дел, не читая подписи.
          <Button
            look="quiet"
            aria-label={visible ? t('common.hidePassword') : t('common.showPassword')}
            title={visible ? t('common.hidePassword') : t('common.showPassword')}
            onClick={() => setShown(!shown)}
            className="ktm-secret-eye px-2"
          >
            <EyeIcon crossed={visible} />
          </Button>
        ) : null}
      </div>
      {hint ? (
        <p id={hintId} className="text-xs text-ink-soft">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="text-xs text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Выбор из перечня — с той же связанной подписью, что и у поля. */
export function Choice({
  label,
  children,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { readonly label: string }): ReactNode {
  const id = useId();
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm text-ink-soft">
        {label}
      </label>
      <select
        id={id}
        {...rest}
        className="rounded-md border border-line bg-surface px-3 py-2 text-ink"
      >
        {children}
      </select>
    </div>
  );
}

/** Переключатель «да/нет». Подпись — часть самого переключателя, промахнуться нечем. */
export function Switch({
  label,
  checked,
  onToggle,
  disabled,
}: {
  readonly label: string;
  readonly checked: boolean;
  readonly onToggle: (next: boolean) => void;
  readonly disabled?: boolean;
}): ReactNode {
  return (
    <label className="flex items-center gap-2 text-sm text-ink">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onToggle(event.currentTarget.checked)}
        className="size-4 accent-[var(--ktm-accent)]"
      />
      <span>{label}</span>
    </label>
  );
}

/**
 * Плашка с объяснением. Беда объявляется голосом сама и настойчиво (`assertive`): она
 * означает, что сделанное не сделалось, и узнать об этом надо сейчас, а не когда-нибудь.
 * Спокойное сообщение объявляется мягко — иначе оно перебивало бы человека на полуслове.
 */
export function Notice({
  kind = 'info',
  children,
  onClose,
  closeLabel,
}: {
  readonly kind?: 'info' | 'error';
  readonly children: ReactNode;
  readonly onClose?: () => void;
  readonly closeLabel?: string;
}): ReactNode {
  const error = kind === 'error';
  return (
    <div
      role={error ? 'alert' : 'status'}
      aria-live={error ? 'assertive' : 'polite'}
      className={`flex items-start justify-between gap-3 rounded-md border px-3 py-2 text-sm ${
        error ? 'border-danger bg-danger-soft text-danger' : 'border-line bg-surface-2 text-ink-soft'
      }`}
    >
      <div>{children}</div>
      {onClose ? (
        <Button look="quiet" onClick={onClose} aria-label={closeLabel} className="shrink-0">
          ✕
        </Button>
      ) : null}
    </div>
  );
}
